import { useMemo, useState } from 'react';

import { GearbeltEvent, GetCurrentWebUserResponseDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { HTTPError } from 'ky';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FileRejection } from 'react-dropzone';

import QuestionIcon from '@/components/icons/question';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Dropzone, DropzoneContent, DropzoneInput } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toaster';
import { cn } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { SignUpSteps, useSignUpStore } from '@/stores/signup.store';
import { useUserStore } from '@/stores/user.store';
import { useWorkspaceSetupStore, WorkspaceSetupSteps } from '@/stores/workspace-setup.store';
import { useEvents } from '@/utils/events';
import { convertLogoUrl } from '@/utils/image-url';
import { useWorkspaceNavigate } from '@/utils/routes';

import { WorkspaceBrandingForm } from './signup-form-utils';

export const WorkspaceBrandingStep = () => {
  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const { signUpStep, setSignUpStep, clearSignUpStore } = useSignUpStore();
  const { workspaceSetupStep, setWorkspaceSetupStep } = useWorkspaceSetupStore();
  const { org, setOrg, user, setUser } = useUserStore();
  const { trackEvent } = useEvents();

  const accept = useMemo(
    () => ({
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    }),
    [],
  );

  const defaultWorkspaceColor = '#2e81da';

  const [brandingForm, setBrandingForm] = useState<WorkspaceBrandingForm>({
    logoUrl: null,
    color: defaultWorkspaceColor,
    newLogo: null,
  });
  const [hasError, setHasError] = useState<{ message: string | null }>({ message: null });
  const [isSaving, setIsSaving] = useState(false);

  async function onDrop(file: File) {
    if (!file) return;

    if (brandingForm.newLogo && brandingForm.logoUrl) {
      URL.revokeObjectURL(brandingForm.logoUrl);
    }

    const areImageDimensionsInvalid = await validateDimensions(file);
    if (areImageDimensionsInvalid) {
      setHasError({ message: areImageDimensionsInvalid });
      toast.error(areImageDimensionsInvalid);
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    setBrandingForm((prev) => ({
      ...prev,
      newLogo: file,
      logoUrl: previewUrl,
    }));
  }

  function onDropRejection(fileRejections: FileRejection[]) {
    setBrandingForm({ ...brandingForm, newLogo: null, logoUrl: null });
    const rejection = fileRejections[0];
    if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
      setHasError({ message: `You can't upload files of this type.` });
      toast.error(`You can't upload files of this type.`);
    } else if (rejection.errors.some((e) => e.code === 'file-too-large')) {
      setHasError({ message: 'File exceeds 5MB.' });
      toast.error('File exceeds 5MB.');
    } else {
      setHasError({ message: 'File is invalid.' });
      toast.error('File is invalid.');
    }
  }

  function validateDimensions(file: File): Promise<string | null> {
    const minDimension = 100;
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        if (img.width < minDimension || img.naturalHeight < minDimension) {
          setBrandingForm({ ...brandingForm, newLogo: null, logoUrl: null });
          resolve(`File must be greater than ${minDimension}x${minDimension} pixels.`);
        } else {
          resolve(null);
        }
      };
      img.onerror = () => resolve('Could not read image dimensions.');
    });
  }

  function onSkipOrNext() {
    if (workspaceSetupStep === WorkspaceSetupSteps.WorkspaceBranding) {
      setWorkspaceSetupStep(WorkspaceSetupSteps.WelcomeModal);
    } else {
      setSignUpStep(SignUpSteps.Complete);
      clearSignUpStore();
      navigate('/dashboard');
    }
  }

  const { mutateAsync: brandingUpdate } = useMutation({
    mutationFn: (form: WorkspaceBrandingForm) => {
      if (!org?.id) {
        throw new Error('Workspace ID is missing');
      }

      if (typeof form.newLogo !== 'string') throw new Error('File format is invalid');

      return ApiService.UpdateWorkspaceBranding({
        workspaceId: org?.id as string,
        file: form.newLogo,
        color: form.color,
      });
    },

    onSuccess: () => {
      onSkipOrNext();
    },

    onError: async (error: HTTPError) => {
      const errorMessage = await error.response.json<{ message: string }>().then((json) => json.message);
      toast.error(`Error uploading logo: ${errorMessage}`);
    },
  });

  async function onSubmit() {
    setIsSaving(true);
    try {
      if (brandingForm.newLogo && org?.id) {
        const logoFileAsString = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(brandingForm.newLogo as Blob);
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = (error) => reject(error);
        });

        const response = await brandingUpdate({ ...brandingForm, newLogo: logoFileAsString });
        const { logoUrl } = await response.json();

        const updatedWorkspace = { ...org, logoUrl: logoUrl as string, color: brandingForm.color };
        setOrg(updatedWorkspace);
        setUser({ ...user } as GetCurrentWebUserResponseDTO);
      }
    } catch (error) {
      console.error('Failed to save branding:', error);
    } finally {
      setIsSaving(false);
    }
  }

  function onUploadLogoInteraction() {
    trackEvent(GearbeltEvent.CTAClicked, {
      Context: 'Sign Up Upload Logo',
    });
  }

  return (
    (signUpStep === SignUpSteps.WorkspaceBranding || workspaceSetupStep === WorkspaceSetupSteps.WorkspaceBranding) && (
      <Dialog open={signUpStep === SignUpSteps.WorkspaceBranding || workspaceSetupStep === WorkspaceSetupSteps.WorkspaceBranding}>
        <DialogContent className="h-[550px] w-[400px] gap-0 p-8">
          <DialogHeader className="flex flex-col items-center justify-center space-y-0 pb-2">
            <DialogTitle hideCloseButton={true} className={cn('text-default-text pb-2 text-xl')}>
              <span className="font-bold"> Setup your workspace</span>
            </DialogTitle>
            <p className="box-content text-center text-sm">Upload your logo so all your forms are branded</p>
            <p className="text-secondary-text text-sm">{'(This can be changed later)'}</p>
          </DialogHeader>
          <div className="text-default-text flex flex-col items-center gap-2 font-normal">
            <Dropzone
              disabled={isSaving}
              accept={accept}
              maxSize={5 * 1024 * 1024}
              maxFiles={1}
              multiple={false}
              onDropAccepted={(files) => {
                setHasError({ message: null });
                onDrop(files[0]);
              }}
              onDropRejected={(fileRejections) => {
                onDropRejection(fileRejections);
              }}>
              <DropzoneContent
                className={cn(
                  clsx('mt-2 flex h-fit w-full items-center justify-center p-1', {
                    'border-none': brandingForm.logoUrl,
                    'border-grey-4 bg-grey-1 border-2 border-dashed': !brandingForm.logoUrl,
                    'border-default-red bg-default-red/15 border-2': hasError.message,
                  }),
                )}>
                <>
                  {brandingForm.newLogo === null ? (
                    <div className="flex flex-col gap-4 py-6">
                      <Button type="button" size="sm" className="flex-grow px-[16px]" onClick={onUploadLogoInteraction}>
                        Upload your logo
                      </Button>
                      <p className="text-secondary-text text-sm">Or drag it into this box</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-around gap-6">
                      {brandingForm.logoUrl && <img src={convertLogoUrl(brandingForm.logoUrl)} alt="uploaded logo image" className="max-h-36" />}
                      <Button type="button" size="sm" className="rounded" onClick={onUploadLogoInteraction}>
                        Upload a different logo
                      </Button>
                    </div>
                  )}

                  <DropzoneInput />
                </>
              </DropzoneContent>
            </Dropzone>

            {!brandingForm.newLogo && (
              <div className="flex items-center self-end">
                {hasError.message && <div className="text-default-red flex items-center justify-center gap-2 text-sm">{hasError.message}</div>}
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      <div className="flex flex-col items-start justify-start text-xs">
                        <p>Logo must be:</p>
                        <ul className="list-disc text-left">
                          <li>Less than 5MB</li>
                          <li>Greater than 100x100 pixels</li>
                          <li>Either a JPG, JPEG, PNG or BMP file</li>
                        </ul>
                      </div>
                    </Tooltip>
                  }>
                  <Button type="button" variant="grey" size="sm" className="bg-grey-1 px-1.4 text-default-text self-end py-[4px]">
                    <QuestionIcon className="box-content self-end pr-1" />
                    View Requirements
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </div>
          <div className="flex items-center justify-start gap-1 pt-2">
            <Input
              type="color"
              containerClassName="w-fit"
              className="[&::-webkit-color-swatch]:border-grey-3 h-10 w-9 border-none bg-transparent p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border-2"
              value={brandingForm.color || defaultWorkspaceColor}
              onChange={(e) => setBrandingForm({ ...brandingForm, color: e.target.value })}
            />
            <p className="text-sm">Pick your brand colour</p>
          </div>

          <div className="flex flex-col gap-2">
            <Button size="lg" className="mt-3 w-full p-2" type="button" disabled={!brandingForm.newLogo} onClick={onSubmit}>
              Get Started
            </Button>
            <Button
              variant="grey"
              size="lg"
              className="w-full py-2 font-medium"
              type="button"
              onClick={() => {
                trackEvent(GearbeltEvent.CTAClicked, {
                  Context: 'Sign Up Skip Logo',
                });
                onSkipOrNext();
              }}>
              Skip this step
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  );
};
